// Per-file calibration status chip for the observation file cards. Renders one
// of three mutually-exclusive states, in precedence:
//   1. not calibratable        → the existing "No Cal" chip (MissingCalibrationChip)
//   2. calibratable but stale   → "Cal · stale" (only match is an expired master)
//   3. calibratable and clean   → the green "Cal" chip
// Reuses MissingCalibrationChip verbatim for state 1 so the "missing" wording
// and look stay single-sourced (it self-gates to null when calibratable=true).
//
// Used by the two user observation views (ObservationDetail, ObservationFiles).
// The operator preview (EmbeddedObservationDetail) and the "My Files" grid
// (FileGrid) render their own chips off different coverage signals and do NOT
// surface the stale state yet — see the follow-ups noted in the fix plan.
//
// Name avoids collision with calibrationBlockedReason.js's `calibrationStatusChip`
// (that one is for calibration-library accumulation groups, not per-image files).

import React from 'react';
import { Chip, Tooltip, Box } from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';
import MissingCalibrationChip from './MissingCalibrationChip';
import { staleCalibrationReason } from '../../utils/staleCalibrationReason';

/**
 * @param {{ file: object, sx?: object }} props
 *   `file` is a userfile shape with `calibratable` + `calibratability` set by
 *   userFilesRoutes. `sx` is merged onto the green/stale chips so callers can
 *   size the chip to their card (the "No Cal" branch keeps its own styling).
 */
const FileCalibrationChip = ({ file, sx }) => {
    // State 1: not calibratable → delegate to the existing chip (may be null).
    if (!file?.calibratable) {
        return <MissingCalibrationChip file={file} />;
    }

    // State 2: calibratable but only an expired master matched.
    const stale = staleCalibrationReason(file);
    if (stale) {
        const tooltipContent = (
            <Box sx={{ whiteSpace: 'pre-line' }}>
                {stale.tooltipLines.join('\n')}
            </Box>
        );
        return (
            <Tooltip title={tooltipContent} placement="top">
                <Chip
                    label={stale.label}
                    size="small"
                    color={stale.color}
                    icon={<HistoryIcon />}
                    variant="outlined"
                    sx={sx}
                />
            </Tooltip>
        );
    }

    // State 3: clean — the existing green "Cal" chip (compact). Positioning and
    // opacity come from the wrapping Box at the call site (see the two render
    // sites), so this only carries sizing.
    return (
        <Chip
            label="Cal"
            size="small"
            color="success"
            sx={{ height: 20, fontSize: '0.65rem', ...sx }}
        />
    );
};

export default FileCalibrationChip;
